import React from 'react';
import styled from 'styled-components';
import {
  Colors,
  Text,
  IcPaymentTypeCash,
  IcPaymentTypeCreditCard,
  IcPaymentTypeLoyaltyPoint,
  IcPaymentTypeGiftCard,
  IcPaymentTypeEpay,
  Button,
  Box,
} from '@fozito/common-ui';
import { formatCurrency } from 'utils/formatCurrency';
import { ICustomerPaymentDetail } from 'modules/POS/Cashier/socket.interface';
import { IMultiCardResDataItem } from 'modules/POS/Cashier/payments/MultiCards/MultipleCardPayment/index.interface';
import ebt_icon from './EBT.svg';

const PaymentMethodContainer = styled.div<{ $isEven: boolean }>`
  display: flex;
  height: 80px;
  align-items: center;
  justify-content: space-between;
  background: ${(props) =>
    props.$isEven ? Colors.neutral.bg[2] : Colors.neutral.bg[1]};
  border: 1px solid ${Colors.neutral.border[3]};
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${Colors.neutral.bg[3]};
  }
`;

const PaymentMethodLeft = styled.div`
  display: flex;
  align-items: center;
`;

const PaymentMethodLabel = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  margin-left: 8px;
`;

const AcceptButtons = styled.div`
width: 50%;
display: flex;
gap: 16px;
align-self: center;
padding-bottom: 16px;
button {
  flex: 1;
}
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
`;

interface PaymentMethodProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  loyaltyAvailable?: number;
  chance?: number;
  isEven?: boolean;
  giftCard?: boolean;
  giftCardBalance?: number;
  giftCardAvailable?: number;
}

export const PaymentMethod: React.FC<PaymentMethodProps> = ({
  icon,
  label,
  value,
  loyaltyAvailable,
  chance,
  isEven = false,
  giftCard,
  giftCardBalance,
  giftCardAvailable,
}) => (
  <PaymentMethodContainer $isEven={isEven}>
    <PaymentMethodLeft>
      {icon}
      <PaymentMethodLabel>
        <Text title_h8_18_medium>{label}</Text>
        {loyaltyAvailable !== undefined && (
          <Text color_secondary_3 content_content_2_16_medium>
            (Avail: {formatCurrency(loyaltyAvailable)})
          </Text>
        )}
        {chance !== undefined && (
          <Text color_secondary_3 content_content_2_16_medium>
            (Chance: {formatCurrency(-chance)})
          </Text>
        )}
        {giftCard && (
          <Box display='flex' alignItems='center' gap={16}>
            {giftCardAvailable && <Text color_secondary_3 content_content_2_16_medium>
              (Avail: {formatCurrency(giftCardAvailable)})
            </Text>}
            {giftCardBalance && <Text color_secondary_3 content_content_2_16_medium>
              (Balance: {formatCurrency(giftCardBalance)})
            </Text>}
          </Box>
        )}
      </PaymentMethodLabel>
    </PaymentMethodLeft>
    <Text title_h8_18_medium>{formatCurrency(value)}</Text>
  </PaymentMethodContainer>
);

interface PaymentMethodsProps {
  payments?: ICustomerPaymentDetail;
  loyaltyAvailable?: number;
  total?: number;
  cards?: IMultiCardResDataItem[];
  multiCards?: boolean;

  onCancel?: () => void;
  onConfirm?: () => void;
}

const PaymentMethods: React.FC<PaymentMethodsProps> = ({
  payments,
  loyaltyAvailable,
  total,
  onCancel,
  onConfirm,
  cards,
  multiCards
}) => {
  return (
    <Container>
      <Text title_h2_32_semibold style={{ margin: 24, fontSize: 70 }}>
        {formatCurrency(total)}
      </Text>
      {multiCards ? <>
        {!cards?.length && (
          <PaymentMethod
            icon={<IcPaymentTypeCreditCard />}
            label="Card"
            value={payments?.card || 0}
            isEven
          />
        )}

        {cards?.map(o => (
          <PaymentMethod
            key={o.id}
            icon={<IcPaymentTypeCreditCard />}
            label={`Card ${o.last4 || '--'}`}
            value={o.amount || 0}
            isEven

          />
        ))}
      </> : <>
        <PaymentMethod
          icon={<img src={ebt_icon} alt='ebt' style={{ width: 24, height: 'auto' }} />}
          label="EBT-Food"
          value={payments?.ebtFood || 0}
        />
        <PaymentMethod
          icon={<IcPaymentTypeCash />}
          label="Cash"
          value={payments?.cash || 0}
          chance={payments?.chanceCash || 0}
        />
        <PaymentMethod
          icon={<IcPaymentTypeCreditCard />}
          label="Card"
          value={payments?.card || 0}
        />
        <PaymentMethod
          icon={<IcPaymentTypeLoyaltyPoint />}
          label="Loyalty"
          value={payments?.loyalty || 0}
          loyaltyAvailable={loyaltyAvailable}
        />
        <PaymentMethod
          icon={<IcPaymentTypeGiftCard />}
          label="Gift Card"
          value={payments?.giftCard || 0}
          giftCard={!!payments?.giftCard}
          giftCardAvailable={payments?.giftCardAvailable}
          giftCardBalance={payments?.giftCardBalance}
        />
        <PaymentMethod
          icon={<IcPaymentTypeEpay />}
          label="E-Pays"
          value={payments?.checkOthers || 0}
        />
      </>}
      <Box flex={1} />
      <AcceptButtons>
        {onConfirm && <Button type_cancel onClick={onCancel}>Cancel</Button>}
        {onConfirm && <Button type_primary onClick={onConfirm}>Ok</Button>}
      </AcceptButtons>
    </Container>
  );
};

export default PaymentMethods;